import { useState } from 'react'
import { Building2, ChevronDown, ChevronRight, Users, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSelectableDepartmentTree } from '@/hooks/useDepartments'
import { findDepartmentById } from '@/lib/departments'
import { cn } from '@/lib/utils'
import type { ApiDepartment, DepartmentScopeValue } from '@/types/department'

interface DepartmentScopeFilterProps {
	value: DepartmentScopeValue
	onChange: (value: DepartmentScopeValue) => void
	/** 是否展示「无归属」选项（超管专属，用于筛选 user_id=0 的迁移客户） */
	enableUnassigned?: boolean
}

function SelectableTreeNode({
	node,
	selectedId,
	onSelect,
	level = 0,
}: {
	node: ApiDepartment
	selectedId?: number
	onSelect: (departmentId: number) => void
	level?: number
}) {
	const [isExpanded, setIsExpanded] = useState(level < 2)
	const hasChildren = !!node.children?.length
	const isSelected = selectedId === node.id

	return (
		<div className="select-none">
			<div className="flex items-center gap-1" style={{ paddingLeft: `${level * 16 + 8}px` }}>
				{hasChildren ? (
					<button
						type="button"
						className="h-4 w-4 shrink-0"
						aria-label={isExpanded ? `折叠${node.name}` : `展开${node.name}`}
						aria-expanded={isExpanded}
						onClick={() => {
							setIsExpanded((current) => !current)
						}}
					>
						{isExpanded ? (
							<ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
						) : (
							<ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
						)}
					</button>
				) : (
					<span className="h-4 w-4 shrink-0" aria-hidden="true" />
				)}
				<button
					type="button"
					className={cn(
						'flex min-w-0 flex-1 items-center gap-1 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
						isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
					)}
					onClick={() => onSelect(node.id)}
				>
					<span className="flex-1 truncate font-medium">{node.name}</span>
					<span className="flex items-center gap-0.5 text-xs text-muted-foreground">
						<Users className="h-3 w-3" />
						{node.user_count || 0}
					</span>
				</button>
			</div>
			{hasChildren && isExpanded && (
				<div>
					{node.children?.map((child) => (
						<SelectableTreeNode
							key={child.id}
							node={child}
							selectedId={selectedId}
							onSelect={onSelect}
							level={level + 1}
						/>
					))}
				</div>
			)}
		</div>
	)
}

export function DepartmentScopeFilter({ value, onChange, enableUnassigned = false }: DepartmentScopeFilterProps) {
	const [isOpen, setIsOpen] = useState(false)
	const { data: treeData, isLoading, isError, error, refetch } = useSelectableDepartmentTree()

	const selectedDepartment = findDepartmentById(treeData, value.department_id)

	const handleScopeChange = (scope: string) => {
		if (scope === 'my') {
			onChange({ scope: 'my' })
			setIsOpen(false)
			return
		}
		if (scope === 'unassigned') {
			onChange({ scope: 'unassigned' })
			setIsOpen(false)
			return
		}

		onChange({ scope: 'department', department_id: value.department_id })
	}

	const handleSelectDepartment = (departmentId: number) => {
		onChange({ scope: 'department', department_id: departmentId })
		setIsOpen(false)
	}

	const handleClearDepartment = () => {
		onChange({ scope: 'department' })
	}

	const displayText = () => {
		if (value.scope === 'my') return '只看我的'
		if (value.scope === 'unassigned') return '无归属客户'
		return selectedDepartment?.name || '我的管理范围'
	}

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="w-[220px] justify-start text-left font-normal"
				>
					<Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
					<span className="min-w-0 flex-1 truncate">{displayText()}</span>
					<ChevronDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[360px] p-0" align="start">
			<div className="border-b p-3">
				<Tabs value={value.scope} onValueChange={handleScopeChange}>
					<TabsList className={cn('grid w-full', enableUnassigned ? 'grid-cols-3' : 'grid-cols-2')}>
						<TabsTrigger value="my">只看我的</TabsTrigger>
						<TabsTrigger value="department">按部门</TabsTrigger>
						{enableUnassigned && <TabsTrigger value="unassigned">无归属</TabsTrigger>}
					</TabsList>
				</Tabs>
			</div>

				{value.scope === 'department' && (
				<>
						<div className="flex items-center justify-between border-b px-3 py-2">
							<div className="min-w-0">
								<p className="text-sm font-medium">部门范围</p>
								<p className="truncate text-xs text-muted-foreground">
									不选择部门时查询我的管理范围
								</p>
							</div>
							{value.department_id && (
								<Button type="button" variant="ghost" size="sm" onClick={handleClearDepartment}>
									<X className="mr-1 h-3 w-3" />
									清除
								</Button>
							)}
						</div>
						<div className="max-h-[320px] overflow-auto p-2">
							{isLoading ? (
								<div className="space-y-2 p-2">
									{Array.from({ length: 3 }).map((_, index) => (
										<Skeleton key={index} className="h-8 w-full" />
									))}
								</div>
							) : isError ? (
								<div className="p-4 text-center">
									<p className="mb-2 text-sm text-destructive">加载部门失败</p>
									<p className="mb-3 text-xs text-muted-foreground">
										{error instanceof Error ? error.message : '请稍后重试'}
									</p>
									<Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
										重试
									</Button>
								</div>
							) : !treeData?.length ? (
								<div className="p-4 text-center text-sm text-muted-foreground">
									暂无可筛选下级部门
								</div>
							) : (
								treeData.map((node) => (
									<SelectableTreeNode
										key={node.id}
										node={node}
										selectedId={value.department_id}
										onSelect={handleSelectDepartment}
									/>
								))
							)}
						</div>
					</>
				)}
			</PopoverContent>
		</Popover>
	)
}
